import React from 'react';
import { PeriodRangeButton } from './PeriodRangeButton';

/** Granularidades padrão do segmentado */
export type PeriodPickerPreset = 'week' | 'month' | 'year';

/** Modo do período — estruturalmente compatível com `DashboardPeriodMode` de @troia-v3/types */
export type PeriodPickerMode = PeriodPickerPreset | 'custom';

/**
 * Valor do seletor. `P` = granularidades do segmentado — default Semana/Mês/Ano;
 * telas com outra régua (ex.: Semana/Mês/Trimestre) passam `segments` próprios.
 */
export interface PeriodPickerValue<P extends string = PeriodPickerPreset> {
  mode: P | 'custom';
  /** yyyy-mm-dd — presentes quando mode === 'custom' */
  start?: string;
  end?: string;
}

export interface PeriodPickerSegment<P extends string = PeriodPickerPreset> {
  key: P;
  label: string;
}

export interface PeriodPickerProps<P extends string = PeriodPickerPreset> {
  value: PeriodPickerValue<P>;
  onChange: (value: PeriodPickerValue<P>) => void;
  /** Granularidades do segmentado (default: Semana/Mês/Ano) */
  segments?: ReadonlyArray<PeriodPickerSegment<P>>;
  /** Rótulo acessível do grupo segmentado (default: "Período") */
  ariaLabel?: string;
  className?: string;
  /** Prefixo dos testids internos (default: "period-picker") */
  'data-testid'?: string;
}

const DEFAULT_SEGMENTS: ReadonlyArray<PeriodPickerSegment> = [
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mês' },
  { key: 'year', label: 'Ano' },
];

/**
 * Seletor de período do Design System (F81) — granularidade universal
 * Semana/Mês/Ano + intervalo Personalizado.
 *
 * Segmentado acessível (aria-pressed + hover ghost, herdado do Segmented do
 * dashboard de suporte) tokenizado com var(--theme-*); o botão "Personalizado"
 * e o popover de datas moram em `PeriodRangeButton`.
 */
export const PeriodPicker = <P extends string = PeriodPickerPreset>({
  value,
  onChange,
  // Sem `segments`, P é o default (Semana/Mês/Ano) — o cast só vale nesse caso.
  segments = DEFAULT_SEGMENTS as unknown as ReadonlyArray<PeriodPickerSegment<P>>,
  ariaLabel = 'Período',
  className,
  'data-testid': testId = 'period-picker',
}: PeriodPickerProps<P>): React.ReactElement => (
  <div className={`flex items-center gap-2 ${className ?? ''}`} data-testid={testId}>
    {/* Segmentado "sunken": pill 32px (38px totais — control_height_38), adapta claro/escuro */}
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center rounded-lg p-[3px] gap-0.5 flex-shrink-0"
      style={{ backgroundColor: 'var(--theme-card-border)' }}
    >
      {segments.map((seg) => {
        const active = value.mode === seg.key;
        return (
          <button
            key={seg.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange({ mode: seg.key })}
            className="px-3 h-[32px] rounded-md text-[13px] font-semibold whitespace-nowrap transition-colors"
            style={{
              backgroundColor: active ? 'var(--theme-card-background)' : 'transparent',
              color: active ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
            }}
            onMouseEnter={(e) => {
              if (!active) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }}
            onMouseLeave={(e) => {
              if (!active) e.currentTarget.style.backgroundColor = 'transparent';
            }}
            data-testid={`${testId}-${seg.key}`}
          >
            {seg.label}
          </button>
        );
      })}
    </div>

    <PeriodRangeButton
      active={value.mode === 'custom'}
      {...(value.start !== undefined ? { start: value.start } : {})}
      {...(value.end !== undefined ? { end: value.end } : {})}
      onApply={({ start, end }) => onChange({ mode: 'custom', start, end })}
      data-testid={testId}
    />
  </div>
);

PeriodPicker.displayName = 'PeriodPicker';
